import {
  Box,
  ListItem,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import React from "react";
import { DealDocumentRowItemActionsMenu } from "../DealDocumentsPage/DealDocumentRowItemActionsMenu";
import DocumentDownloadIcon from "../../../common/icons/document/documentDownloadIcon";
import { downloadDealDocumentVersion } from "../../../../api/deals/documents";
import { useParams } from "react-router-dom";
import { DealDocumentRouteProps } from "../../../../routes/dealsRouting/DealsRoutes";
import { DealDocumentVersionDownloadFormats } from "../../../../api/generated-api";

type Props = {
  version: number;
  name: string;
  date: string;
  sourceExtension: string;
  selected?: boolean;
  onClick?: () => void;
};

const DocumentVersionRow = ({
  version,
  name,
  date,
  sourceExtension,
  selected = false,
  onClick,
}: Props) => {
  const theme = useTheme();

  const routeParams = useParams();
  const { orgId, dealId, documentId } = routeParams as DealDocumentRouteProps;

  const container: SxProps<Theme> = {
    display: "flex",
    flexDirection: "row",
    padding: "20px",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: selected
      ? theme.palette.secondary.light
      : theme.palette.tertiary.lighter,
    borderBottom: `1px solid ${theme.palette.tertiary.light}`,
    cursor: "pointer",
  };

  const versionNumberContainerAndText: SxProps<Theme> = {
    fontFamily: theme.typography.fontFamily,
    fontWeight: 500,
    fontSize: "45px",
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
  };

  const metaAndOverflowContainer: SxProps<Theme> = {
    display: "flex",
    flexDirection: "row",
    flex: 4,
  };

  const nameAndDateContainer: SxProps<Theme> = {
    flex: "1",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    marginTop: "10px",
    marginRight: "10px",
    marginBottom: "10px",
  };

  const overflowContainer: SxProps<Theme> = {
    display: "flex",
    justifyContent: "flex-end",
    alignContent: "flex-start",
  };

  const overflowRowContainer: SxProps<Theme> = {
    cursor: "pointer",

    "&:hover": {
      backgroundColor: theme.palette.secondary.light,
    },
  };

  const overflowIconContainer: SxProps<Theme> = {
    marginRight: "10px",
  };

  const DealDocumentVersionOverflowMenuItem = ({
    dataTestId,
    text,
    onClick,
  }: {
    dataTestId: string;
    text: string;
    onClick: (event) => void;
  }) => (
    <ListItem
      data-testid={dataTestId}
      sx={overflowRowContainer}
      onClick={onClick}
    >
      <Box sx={overflowIconContainer}>
        <DocumentDownloadIcon />
      </Box>
      <Typography sx={{ color: theme.palette.secondary.dark }}>
        {text}
      </Typography>
    </ListItem>
  );

  const handleDownloadDocumentVersion = (
    downloadType: DealDocumentVersionDownloadFormats
  ) => {
    downloadDealDocumentVersion(
      +orgId,
      +dealId,
      +documentId,
      version,
      downloadType
    );
  };

  return (
    <Box sx={container} onClick={onClick} data-testid="document-version-row">
      <Box
        sx={versionNumberContainerAndText}
        data-testid="document-version-row-version-number"
      >
        {version}
      </Box>
      <Box sx={metaAndOverflowContainer}>
        <Box sx={nameAndDateContainer}>
          <Typography variant="h4" data-testid="document-version-row-name">
            {name}
          </Typography>
          <Typography variant="subtitle2">{date}</Typography>
        </Box>
        <Box sx={overflowContainer}>
          <DealDocumentRowItemActionsMenu dataTestId="document-version-row-overflow-menu">
            {sourceExtension !== "pdf" && (
              //don't show download source if it's a pdf because it's the same as the pdf
              <DealDocumentVersionOverflowMenuItem
                dataTestId="document-version-row-overflow-download-source"
                text={`Download .${sourceExtension}`}
                onClick={() => {
                  handleDownloadDocumentVersion("source");
                }}
              />
            )}
            <DealDocumentVersionOverflowMenuItem
              dataTestId="document-version-row-overflow-download-pdf"
              text="Download .pdf"
              onClick={() => {
                handleDownloadDocumentVersion("pdf");
              }}
            />
          </DealDocumentRowItemActionsMenu>
        </Box>
      </Box>
    </Box>
  );
};

export default DocumentVersionRow;
